/**
 * useXRPushNotifications — v10.10.0
 * Hook cliente para notificaciones push locales durante una sesión XR.
 *
 * Estrategia (sin VAPID / sin push server externo):
 *  1. Cliente solicita permiso `Notification` al montar (idempotente).
 *  2. Se conecta al WebSocket de la sesión (mismo que supervisión).
 *  3. Cuando llega un evento `xr:notification`, `simulation_paused`,
 *     `simulation_resumed`, `instructor_hint`, se forwardea al Service
 *     Worker vía `postMessage` para que muestre la notificación nativa
 *     usando `registration.showNotification()`.
 *
 *  Esto funciona con la pestaña en background o pantalla apagada mientras
 *  el WebSocket permanezca vivo (típico en sesión clínica activa). Al
 *  cerrarse el WS, deja de notificar — es un canal local, no push remoto.
 *
 * Uso:
 *   const push = useXRPushNotifications({ sessionId, accessToken });
 *   push.permission // 'default' | 'granted' | 'denied' | 'unsupported'
 *   push.requestPermission()
 *   push.showLocal({ title, body })  // manual (test)
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

async function ensureSWReady() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return reg;
  } catch {
    return null;
  }
}

export default function useXRPushNotifications({ sessionId, accessToken, enabled = true } = {}) {
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  const [connected, setConnected] = useState(false);
  const [lastNotification, setLastNotification] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const closedByUserRef = useRef(false);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return 'unsupported';
    try {
      const p = await Notification.requestPermission();
      setPermission(p);
      return p;
    } catch {
      return 'denied';
    }
  }, []);

  const showLocal = useCallback(async ({ title, body, tag = 'xr-local', data = {}, icon }) => {
    if (typeof Notification === 'undefined') return false;
    if (Notification.permission !== 'granted') return false;
    const reg = await ensureSWReady();
    const opts = {
      body,
      tag,
      icon: icon || '/images/simuclinic-logo.png',
      badge: '/images/simuclinic-logo.png',
      data: { url: window.location.pathname, ...data },
      renotify: false,
    };
    try {
      if (reg && typeof reg.showNotification === 'function') {
        await reg.showNotification(title, opts);
      } else {
        // Fallback (foreground only)
        new Notification(title, opts);
      }
      setLastNotification({ title, body, tag, at: new Date().toISOString() });
      return true;
    } catch {
      return false;
    }
  }, []);

  /* ---------- Traducción evento WS → notificación ---------- */
  const notifyFromEvent = useCallback((msg) => {
    if (!msg || typeof msg !== 'object') return;
    const et = msg.event_type;
    if (!et) return;
    // Ignorar los propios eventos del alumno
    if (et === 'action_performed' && msg.performed_by === msg.actor_id) return;
    const map = {
      simulation_paused: {
        title: 'Sesión en pausa',
        body: msg.reason || 'El instructor ha pausado la simulación.',
        tag: `session-${msg.session_id || sessionId}-pause`,
      },
      simulation_resumed: {
        title: 'Sesión reanudada',
        body: 'El instructor reanudó la simulación. Continúa.',
        tag: `session-${msg.session_id || sessionId}-resume`,
      },
      instructor_hint: {
        title: msg.hint_type === 'alerta' ? '⚠︎ Aviso del instructor' : 'Mensaje del instructor',
        body: msg.content || 'Nueva sugerencia.',
        tag: `hint-${Date.now()}`,
      },
      chat_message: msg.sender_role !== 'student' && msg.sender_role !== 'estudiante' ? {
        title: `Mensaje de ${msg.sender_name || 'instructor'}`,
        body: msg.content || '',
        tag: `chat-${Date.now()}`,
      } : null,
      'xr:notification': {
        title: msg.title || 'SimuClinic XR',
        body: msg.body || '',
        tag: msg.tag || `xr-notif-${Date.now()}`,
      },
      patient_state_change: msg.new_state ? {
        title: 'Estado del paciente cambió',
        body: `Nuevo estado: ${msg.new_state}${msg.reason ? ` (${msg.reason})` : ''}`,
        tag: `state-${Date.now()}`,
      } : null,
    };
    const spec = map[et];
    if (!spec) return;
    // Solo notificar cuando la pestaña NO está visible o siempre en móvil,
    // para no duplicar UI que ya está en pantalla.
    const shouldNotifyInForeground = et === 'instructor_hint' || et === 'xr:notification' || et === 'simulation_paused';
    if (!shouldNotifyInForeground && document.visibilityState === 'visible') return;
    showLocal(spec);
  }, [sessionId, showLocal]);

  /* ---------- WebSocket ---------- */
  const connect = useCallback(() => {
    if (!enabled || !sessionId || !accessToken) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = (BACKEND_URL || '').replace(/^https?:\/\//, '');
    if (!wsHost) return;
    const url = `${wsProtocol}//${wsHost}/api/ws/simulation/${sessionId}?token=${accessToken}&mode=student&last_seq=0`;
    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        if (!closedByUserRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = setTimeout(connect, 3000);
        }
      };
      ws.onerror = () => { /* noop, close handler manages reconnect */ };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          notifyFromEvent(msg);
        } catch { /* not JSON */ }
      };
    } catch { /* noop */ }
  }, [enabled, sessionId, accessToken, notifyFromEvent]);

  useEffect(() => {
    closedByUserRef.current = false;
    connect();
    return () => {
      closedByUserRef.current = true;
      clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) try { wsRef.current.close(); } catch { /* noop */ }
      wsRef.current = null;
    };
  }, [connect]);

  return {
    permission,
    connected,
    lastNotification,
    requestPermission,
    showLocal,
  };
}
